<template>
    <Modal2
        :title="$t('googleDrive.selectFile')"
        :description="$t('googleDrive.selectFileFromGoogleDrive')"
        @cancel="handleCancel"
        @ok="handleConfirm"
        class-name="google-drive-picker-modal"
        :okText="$t('googleDrive.upload')"
        :cancelText="$t('message.cancel')"
        :okDisabled="selectedFiles.length === 0 || uploading"
        :okLoading="uploading"
    >
        <div class="google-drive-picker">
            <!-- Upload Loading Overlay -->
            <div class="upload-overlay" v-if="uploading">
                <a-spin size="large" />
                <p>{{ $t("googleDrive.downloadingFiles") }}</p>
                <p class="upload-progress">{{ uploadProgress }}</p>
            </div>

            <!-- Kanal Seçimi -->
            <div class="channel-selector" v-if="channels.length > 0">
                <label>{{ $t("googleDrive.selectChannel") }}</label>
                <a-select v-model="selectedChannelId" style="width: 100%" @change="onChannelChange">
                    <a-select-option v-for="channel in channels" :key="channel._id" :value="channel._id">
                        <div class="channel-option">
                            <span>{{ channel.name }}</span>
                            <span class="channel-email">({{ channel.payload && channel.payload.email }})</span>
                            <a-tag v-if="isMyChannel(channel)" color="blue" size="small">{{ $t("googleDrive.myChannel") }}</a-tag>
                        </div>
                    </a-select-option>
                </a-select>
            </div>

            <div class="no-channels" v-if="channels.length === 0">
                <a-icon type="warning" />
                <p>{{ $t("googleDrive.noChannelsAvailable") }}</p>
            </div>

            <template v-else>
                <!-- Tab'lar -->
                <div class="tabs">
                    <a-radio-group v-model="activeTab" button-style="solid" size="small">
                        <a-radio-button value="myDrive">{{ $t("googleDrive.myDrive") }}</a-radio-button>
                        <a-radio-button value="sharedDrives">{{ $t("googleDrive.sharedDrives") }}</a-radio-button>
                    </a-radio-group>
                    <a-input-search
                        v-model="searchQuery"
                        :placeholder="$t('googleDrive.searchFiles')"
                        style="width: 200px"
                        @search="handleSearch"
                        allowClear
                        size="small"
                    />
                </div>

                <!-- Shared Drives Listesi -->
                <div class="shared-drives-list" v-if="activeTab === 'sharedDrives' && !selectedDriveId">
                    <div class="loading" v-if="loadingDrives">
                        <a-spin size="small" />
                    </div>
                    <div class="drives-grid" v-else>
                        <div
                            class="drive-card"
                            v-for="drive in sharedDrives"
                            :key="drive.id"
                            @click="selectSharedDrive(drive)"
                        >
                            <a-icon type="folder" class="drive-icon" />
                            <span class="drive-name">{{ drive.name }}</span>
                        </div>
                        <div class="empty-state" v-if="sharedDrives.length === 0">
                            <p>{{ $t("googleDrive.noSharedDrives") }}</p>
                        </div>
                    </div>
                </div>

                <!-- Dosya Listesi -->
                <div class="files-container" v-else>
                    <!-- Breadcrumb -->
                    <div class="breadcrumb-container" v-if="!isSearchMode">
                        <a-breadcrumb>
                            <a-breadcrumb-item>
                                <a @click="navigateToRoot">
                                    <a-icon type="home" />
                                </a>
                            </a-breadcrumb-item>
                            <a-breadcrumb-item v-for="folder in breadcrumbs" :key="folder.id">
                                <a @click="navigateToFolder(folder.id, folder.name)">{{ folder.name }}</a>
                            </a-breadcrumb-item>
                        </a-breadcrumb>
                    </div>

                    <div class="loading" v-if="loading">
                        <a-spin size="small" />
                    </div>

                    <div class="files-grid" v-else>
                        <div
                            class="file-card"
                            :class="{ selected: isSelected(file), folder: isFolder(file) }"
                            v-for="file in files"
                            :key="file.id"
                            @click="handleFileClick($event, file)"
                            @dblclick="handleFileDblClick($event, file)"
                        >
                            <div class="file-icon">
                                <a-icon v-if="isFolder(file)" type="folder" theme="filled" />
                                <img v-else-if="file.thumbnailLink" :src="file.thumbnailLink" />
                                <a-icon v-else :type="getFileIcon(file.mimeType)" />
                            </div>
                            <div class="file-name" :title="file.name">{{ file.name }}</div>
                        </div>
                        <div class="empty-state" v-if="files.length === 0 && !loading">
                            <p>{{ $t("googleDrive.noFiles") }}</p>
                        </div>
                    </div>

                    <div class="load-more" v-if="nextPageToken && !loading">
                        <a-button size="small" @click="loadMore" :loading="loadingMore">
                            {{ $t("googleDrive.loadMore") }}
                        </a-button>
                    </div>
                </div>

                <!-- Seçili Dosyalar -->
                <div class="selected-files" v-if="selectedFiles.length > 0">
                    <span>{{ $t("googleDrive.selectedFiles") }}: {{ selectedFiles.length }}</span>
                    <div class="selected-file-tags">
                        <a-tag
                            v-for="fileId in selectedFiles"
                            :key="fileId"
                            closable
                            @close="removeSelection(fileId)"
                        >
                            {{ getFileName(fileId) }}
                        </a-tag>
                    </div>
                </div>
            </template>
        </div>

        <!-- File Preview Modal -->
        <FilePreviewModal
            :visible="previewVisible"
            :files="previewableFiles"
            :initial-index="previewIndex"
            :channel-id="selectedChannelId"
            @close="closePreview"
            @downloaded="onFileDownloaded"
        />
    </Modal2>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import Modal2 from "../common/Modal2.vue";
import FilePreviewModal from "./FilePreviewModal.vue";
import { GOOGLE_DRIVE_API } from "../../api/google-drive";

export default {
    name: "GoogleDriveFilePicker",
    components: {
        Modal2,
        FilePreviewModal,
    },
    props: {
        multiple: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedChannelId: null,
            activeTab: "myDrive",
            searchQuery: "",
            isSearchMode: false,
            loading: false,
            loadingMore: false,
            loadingDrives: false,
            uploading: false,
            uploadedCount: 0,
            totalToUpload: 0,
            files: [],
            sharedDrives: [],
            selectedFiles: [],
            nextPageToken: null,
            currentFolderId: null,
            selectedDriveId: null,
            breadcrumbs: [],
            previewVisible: false,
            previewIndex: 0,
        };
    },
    computed: {
        ...mapState({
            allChannels: (state) => state.channel.channels.filter((x) => x.type === "google-drive" && x.enabled),
            isAdmin: (state) => state.auth.adminUser !== null,
            user: (state) => state.auth.user,
        }),
        ...mapGetters({
            isChannelVisibleForMe: "channel/isChannelVisibleForMe",
        }),
        channels() {
            return this.allChannels.filter((channel) => {
                return this.isAdmin || this.$_.get(this.user, "isOwner", false) || this.isChannelVisibleForMe(channel);
            });
        },
        uploadProgress() {
            if (this.totalToUpload === 0) return "";
            return this.uploadedCount + " / " + this.totalToUpload;
        },
        previewableFiles() {
            return this.files.filter(function(f) {
                return f.mimeType !== "application/vnd.google-apps.folder";
            });
        },
    },
    methods: {
        async fetchFiles() {
            if (!this.selectedChannelId) return;
            this.loading = true;
            try {
                const response = await GOOGLE_DRIVE_API.listFiles(
                    this.selectedChannelId,
                    this.currentFolderId,
                    null,
                    this.selectedDriveId
                );
                this.files = response.data.files || [];
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingFiles"));
            } finally {
                this.loading = false;
            }
        },
        async loadMore() {
            if (!this.nextPageToken) return;
            this.loadingMore = true;
            try {
                const response = await GOOGLE_DRIVE_API.listFiles(
                    this.selectedChannelId,
                    this.currentFolderId,
                    this.nextPageToken,
                    this.selectedDriveId
                );
                this.files = this.files.concat(response.data.files || []);
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingFiles"));
            } finally {
                this.loadingMore = false;
            }
        },
        async fetchSharedDrives() {
            if (!this.selectedChannelId) return;
            this.loadingDrives = true;
            try {
                const response = await GOOGLE_DRIVE_API.listSharedDrives(this.selectedChannelId);
                this.sharedDrives = response.data.drives || [];
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorLoadingDrives"));
            } finally {
                this.loadingDrives = false;
            }
        },
        async handleSearch() {
            if (!this.searchQuery.trim()) {
                this.isSearchMode = false;
                this.fetchFiles();
                return;
            }
            this.isSearchMode = true;
            this.loading = true;
            try {
                const response = await GOOGLE_DRIVE_API.searchFiles(
                    this.selectedChannelId,
                    this.searchQuery,
                    null,
                    this.selectedDriveId
                );
                this.files = response.data.files || [];
                this.nextPageToken = response.data.nextPageToken;
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorSearching"));
            } finally {
                this.loading = false;
            }
        },
        handleFileClick(event, file) {
            // Cmd (Mac) veya Ctrl (Windows/Linux) + click ile yeni sekmede aç
            if (event.metaKey || event.ctrlKey) {
                if (!this.isFolder(file)) {
                    this.openInNewTab(file);
                }
                return;
            }
            if (this.isFolder(file)) return;
            if (this.multiple) {
                var index = this.selectedFiles.indexOf(file.id);
                if (index > -1) {
                    this.selectedFiles.splice(index, 1);
                } else {
                    this.selectedFiles.push(file.id);
                }
            } else {
                this.selectedFiles = [file.id];
            }
        },
        handleFileDblClick(event, file) {
            if (this.isFolder(file)) {
                this.navigateToFolder(file.id, file.name);
            } else {
                this.openPreview(file);
            }
        },
        openInNewTab(file) {
            var url = file.webViewLink;
            if (!url) {
                url = "https://drive.google.com/file/d/" + file.id + "/view";
            }
            window.open(url, "_blank");
        },
        openPreview(file) {
            var nonFolderFiles = this.files.filter(function(f) {
                return f.mimeType !== "application/vnd.google-apps.folder";
            });
            var fileIndex = nonFolderFiles.findIndex(function(f) {
                return f.id === file.id;
            });
            this.previewIndex = fileIndex >= 0 ? fileIndex : 0;
            this.previewVisible = true;
        },
        closePreview() {
            this.previewVisible = false;
        },
        onFileDownloaded(asset) {
            this.$message.success(this.$t("googleDrive.fileDownloaded"));
        },
        navigateToFolder(folderId, folderName) {
            this.currentFolderId = folderId;
            if (folderName) {
                const existingIndex = this.breadcrumbs.findIndex((b) => b.id === folderId);
                if (existingIndex > -1) {
                    this.breadcrumbs = this.breadcrumbs.slice(0, existingIndex + 1);
                } else {
                    this.breadcrumbs.push({ id: folderId, name: folderName });
                }
            }
            this.fetchFiles();
        },
        navigateToRoot() {
            this.currentFolderId = null;
            this.selectedDriveId = null;
            this.breadcrumbs = [];
            this.fetchFiles();
        },
        selectSharedDrive(drive) {
            this.selectedDriveId = drive.id;
            this.breadcrumbs = [{ id: drive.id, name: drive.name }];
            this.fetchFiles();
        },
        onChannelChange() {
            this.files = [];
            this.sharedDrives = [];
            this.selectedFiles = [];
            this.breadcrumbs = [];
            this.currentFolderId = null;
            this.selectedDriveId = null;
            this.isSearchMode = false;
            this.searchQuery = "";
            this.fetchFiles();
        },
        isFolder(file) {
            return file.mimeType === "application/vnd.google-apps.folder";
        },
        isSelected(file) {
            return this.selectedFiles.includes(file.id);
        },
        removeSelection(fileId) {
            const index = this.selectedFiles.indexOf(fileId);
            if (index > -1) {
                this.selectedFiles.splice(index, 1);
            }
        },
        getFileName(fileId) {
            const file = this.files.find((f) => f.id === fileId);
            return file ? file.name : fileId;
        },
        getFileIcon(mimeType) {
            if (!mimeType) return "file";
            if (mimeType.includes("image")) return "file-image";
            if (mimeType.includes("video")) return "video-camera";
            if (mimeType.includes("audio")) return "sound";
            if (mimeType.includes("pdf")) return "file-pdf";
            return "file";
        },
        isMyChannel(channel) {
            const userId = this.$_.get(this.user, "_id");
            return channel.owner === userId;
        },
        getDefaultChannelId() {
            // 1. localStorage'dan en son kullanılan kanalı kontrol et
            const lastUsedChannelId = localStorage.getItem("lastUsedGoogleDriveChannel");
            if (lastUsedChannelId) {
                const lastUsedChannel = this.channels.find((c) => c._id === lastUsedChannelId);
                if (lastUsedChannel) return lastUsedChannel._id;
            }
            // 2. Kullanıcının owner olduğu kanalı seç
            const userId = this.$_.get(this.user, "_id");
            const myChannel = this.channels.find((c) => c.owner === userId);
            if (myChannel) return myChannel._id;
            // 3. İlk kanalı seç
            return this.channels.length > 0 ? this.channels[0]._id : null;
        },
        saveLastUsedChannel(channelId) {
            localStorage.setItem("lastUsedGoogleDriveChannel", channelId);
        },
        handleCancel() {
            this.$emit("cancel");
        },
        async handleConfirm() {
            if (this.selectedFiles.length === 0) return;
            this.uploading = true;
            this.uploadedCount = 0;
            this.totalToUpload = this.selectedFiles.length;
            const assets = [];
            try {
                for (const fileId of this.selectedFiles) {
                    const response = await GOOGLE_DRIVE_API.downloadFile(this.selectedChannelId, fileId);
                    if (response.data.asset) {
                        assets.push(response.data.asset);
                    }
                    this.uploadedCount++;
                }
                this.saveLastUsedChannel(this.selectedChannelId);
                this.$emit("confirm", assets);
            } catch (error) {
                this.$message.error(this.$t("googleDrive.errorDownloading"));
            } finally {
                this.uploading = false;
                this.uploadedCount = 0;
                this.totalToUpload = 0;
            }
        },
    },
    watch: {
        activeTab(val) {
            this.files = [];
            this.breadcrumbs = [];
            this.currentFolderId = null;
            this.selectedDriveId = null;
            this.isSearchMode = false;
            this.searchQuery = "";
            if (val === "myDrive") {
                this.fetchFiles();
            } else {
                this.fetchSharedDrives();
            }
        },
    },
    mounted() {
        if (this.channels.length > 0) {
            this.selectedChannelId = this.getDefaultChannelId();
            this.fetchFiles();
        }
    },
};
</script>

<style scoped lang="scss">
.google-drive-picker {
    min-height: 400px;
    max-height: 60vh;
    overflow-y: auto;
    position: relative;

    .upload-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.95);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 100;
        border-radius: 8px;

        p {
            margin-top: 16px;
            font-size: 14px;
            color: #333;
        }

        .upload-progress {
            margin-top: 8px;
            font-size: 16px;
            font-weight: 600;
            color: #4285f4;
        }
    }

    .channel-selector {
        margin-bottom: 15px;

        label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
        }

        .channel-option {
            display: flex;
            align-items: center;
            gap: 5px;

            .channel-email {
                color: var(--color-text-secondary);
                font-size: 12px;
            }

            .ant-tag {
                margin-left: auto;
                font-size: 10px;
            }
        }
    }

    .no-channels {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 200px;
        color: var(--color-text-secondary);

        .anticon {
            font-size: 48px;
            margin-bottom: 16px;
            color: #faad14;
        }
    }

    .tabs {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
    }

    .breadcrumb-container {
        margin-bottom: 10px;
        padding: 8px 12px;
        background: var(--color-bg-secondary);
        border-radius: 6px;
    }

    .shared-drives-list,
    .files-container {
        .drives-grid,
        .files-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
            gap: 10px;
        }

        .drive-card,
        .file-card {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 12px 8px;
            background: var(--color-bg-default);
            border: 1px solid var(--color-border-default);
            border-radius: 6px;
            cursor: pointer;
            transition: all 0.2s;

            &:hover {
                border-color: #4285f4;
            }

            &.selected {
                border-color: #4285f4;
                background: rgba(66, 133, 244, 0.1);
            }

            &.folder .file-icon {
                color: #f9ab00;
            }

            .drive-icon,
            .file-icon {
                font-size: 32px;
                color: #5f6368;
                margin-bottom: 8px;

                img {
                    width: 32px;
                    height: 32px;
                    object-fit: cover;
                    border-radius: 4px;
                }
            }

            .drive-name,
            .file-name {
                font-size: 12px;
                text-align: center;
                word-break: break-word;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                max-width: 100%;
            }
        }

        .empty-state {
            grid-column: 1 / -1;
            text-align: center;
            padding: 30px;
            color: var(--color-text-secondary);
        }
    }

    .loading {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 150px;
    }

    .load-more {
        display: flex;
        justify-content: center;
        margin-top: 15px;
    }

    .selected-files {
        margin-top: 15px;
        padding-top: 15px;
        border-top: 1px solid var(--color-border-default);

        span {
            font-weight: 500;
            margin-bottom: 8px;
            display: block;
        }

        .selected-file-tags {
            display: flex;
            flex-wrap: wrap;
            gap: 5px;
        }
    }
}
</style>

<style lang="scss">
.google-drive-picker-modal {
    .ant-modal {
        width: 700px !important;
        max-width: 95vw !important;
    }
}
</style>
